// src/components/maestros/modals/ModalEdicionTrakaPhoto.jsx
/* global $ */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form, FormGroup, Label, Input, Button } from "reactstrap";
import axios from "../../../utils/axiosConfig";
import Swal from "sweetalert2";

/* ==== Utils ==== */
const defaults = {
    IDPhoto: "",
    Placa: "",
    Fecha: "",
    Device: "",
    Evento: "",
    Latitud: "",
    Longitud: "",
    Geocerca: ""
};

const numberOr = (v, fb = null) =>
    Number.isFinite(Number(v)) ? Number(v) : fb;

const toGeo = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};


/* ==== Combobox Geocerca ==== */
function GeocercaSelect({
    options = [],
    value,
    onChange,
    placeholder = "Buscar y seleccionar geocerca…",
    bsSize = "sm"
}) {
    const map = useMemo(
        () => new Map(options.map((o) => [o.id, o.nombre])),
        [options]
    );
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [highlight, setHighlight] = useState(0);
    const wrapRef = useRef(null);

    const selectedLabel = map.get(value) ?? "";
    const norm = (s) =>
        String(s ?? "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase();

    const filtered = useMemo(() => {
        const q = norm(query);
        if (!q) return options;
        return options.filter(
            (o) => norm(o.nombre).includes(q) || String(o.id).includes(query)
        );
    }, [options, query]);

    useEffect(() => {
        const onDoc = (e) => {
            if (!wrapRef.current?.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const selectOption = (opt) => {
        onChange?.(opt.id);
        setQuery("");
        setOpen(false);
    };

    const onKeyDown = (e) => {
        if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
            setOpen(true);
            return;
        }
        if (!open) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            const opt = filtered[highlight];
            if (opt) selectOption(opt);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <div className="geo-wrap" ref={wrapRef}>
            <div className={`geo-input ${open ? "open" : ""}`}>
                <Input
                    type="text"
                    bsSize={bsSize}
                    placeholder={placeholder}
                    value={open ? query : selectedLabel || ""}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setOpen(true);
                        setHighlight(0);
                    }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                    style={{ boxSizing: "border-box", height: 38 }}
                />
                <span className="geo-caret">▾</span>
            </div>

            {open && (
                <div className="geo-menu">
                    {filtered.length === 0 ? (
                        <div className="geo-item empty">Sin resultados</div>
                    ) : (
                        filtered.map((o, i) => (
                            <button
                                key={o.id}
                                type="button"
                                className={`geo-item ${i === highlight ? "hl" : ""
                                    }`}
                                onMouseEnter={() => setHighlight(i)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => selectOption(o)}
                                title={`ID: ${o.id}`}
                            >
                                <span className="text">{o.nombre}</span>
                                <span className="muted">({o.id})</span>
                            </button>
                        ))
                    )}
                </div>
            )}

            <style jsx>{`
                .geo-wrap {
                    position: relative;
                    inline-size: 100%;
                }
                .geo-input {
                    position: relative;
                    inline-size: 100%;
                }
                .geo-caret {
                    position: absolute;
                    right: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                    pointer-events: none;
                    opacity: 0.6;
                }
                .geo-menu {
                    position: absolute;
                    z-index: 20;
                    left: 0;
                    right: 0;
                    background: #fff;
                    border: 1px solid #ced4da;
                    border-top: 0;
                    border-radius: 0 0 0.25rem 0.25rem;
                    max-height: 240px;
                    overflow-y: auto;
                    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
                }
                .geo-item {
                    width: 100%;
                    text-align: left;
                    background: transparent;
                    border: 0;
                    padding: 0.375rem 0.75rem;
                    font-size: 0.875rem;
                    cursor: pointer;
                    display: flex;
                    gap: 0.5rem;
                    justify-content: space-between;
                    align-items: center;
                }
                .geo-item.hl {
                    background: #f1f3f5;
                }
                .geo-item.empty {
                    color: #6c757d;
                    cursor: default;
                }
                .muted {
                    color: #6c757d;
                    font-weight: 400;
                }
            `}</style>
        </div>
    );
}

/* ==== Modal principal ==== */
export default function ModalEdicionTrakaPhoto({ onClose, info }) {
    const init = useMemo(() => ({ ...defaults, ...(info || {}) }), [info]);

    const [form, setForm] = useState({
        IDPhoto: init.Id ?? init.IDPhoto ?? "",
        Placa: init.Placa ?? "",
        // Aquí guardamos la cadena EXACTA que viene del back: "YYYY-MM-DD HH:MM:SS"
        Fecha: init.Fecha ?? init.fecha ?? "",
        Device: init.Device ?? init.Equipo ?? "",
        Evento: init.Evento ?? "",
        Latitud: init.Latitud ?? "",
        Longitud: init.Longitud ?? "",
        Geocerca: toGeo(init.Geocerca ?? init._raw?.Geocerca ?? 0)
    });

    const [errors, setErrors] = useState({});
    const [geocercas, setGeocercas] = useState([]);

    // ref para jQuery datetimepicker
    const fechaRef = useRef(null);



    const mapGeo = (g) => {
        const id =
            g.Id_Geocerca ??
            g.IdGeocerca ??
            g.ID ??
            g.id ??
            g.Id ??
            g.id_geocerca ??
            g.value;
        const nombre =
            g.Nombre ??
            g.name ??
            g.Descripcion ??
            g.descripcion ??
            g.Geocerca ??
            g.label ??
            `#${id}`;
        return { id: Number(id), nombre: String(nombre) };
    };

    const geoOptions = useMemo(
        () =>
            Array.isArray(geocercas)
                ? geocercas
                    .map(mapGeo)
                    .filter((x) => !Number.isNaN(x.id))
                : [],
        [geocercas]
    );

    const getGeocercas = async () => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_SERVER}/maestros/getgeocercas`
            );
            if (!res.data?.success) throw new Error("Respuesta inválida");
            setGeocercas(res.data.data || []);
        } catch (err) {
            console.error("Error obteniendo geocercas:", err);
            setGeocercas([]);
        }
    };

    useEffect(() => {
        getGeocercas();
    }, []);

    useEffect(() => {
        console.log("Inicializando formulario de edición de Foto Traka:", init);
        // cuando cambia info, reseteamos el formulario
        setForm({
            IDPhoto: init.Id ?? init.IDPhoto ?? "",
            Placa: init.Placa ?? "",
            Fecha: init.Fecha ?? init.fecha ?? "",
            Device: init?._raw?.Device ?? init.Equipo ?? "",
            Evento: init.Evento ?? "",
            Latitud: init.Latitud ?? "",
            Longitud: init.Longitud ?? "",
            Geocerca: toGeo(init.Geocerca ?? init._raw?.Geocerca ?? 0)
        });
    }, [init]);

    // Inicializar jQuery datetimepicker para Fecha (24h, sin tocar la hora)
    useEffect(() => {
        if (!fechaRef.current || typeof $ === "undefined") return;

        const pad = (n) => String(n).padStart(2, "0");
        const now = new Date();
        const currentDateTime = `${now.getFullYear()}-${pad(
            now.getMonth() + 1
        )}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(
            now.getMinutes()
        )}:${pad(now.getSeconds())}`;

        const $input = $(fechaRef.current);

        $.datetimepicker.setLocale("es"); // o "en" si prefieres

        $input.datetimepicker({
            format: "Y-m-d H:i:s", // 24h con segundos
            step: 30,
            value: form.Fecha || currentDateTime,
            timepicker: true,
            datepicker: true,
            onChangeDateTime: (dp, $inp) => {
                setForm((prev) => ({
                    ...prev,
                    Fecha: $inp.val() // "YYYY-MM-DD HH:MM:SS"
                }));
            }
        });

        // si el estado cambia desde fuera (por otro registro), actualizamos el input
        $input.val(form.Fecha || currentDateTime);

        return () => {
            try {
                $input.datetimepicker("destroy");
            } catch (err) {
                // por si ya está destruido
            }
        };
        // solo al montar y cuando cambia el registro (init / form.Fecha inicial)

    }, [init.Id]); // usar un identificador del registro para re-inicializar

    // mantener fecha del estado -> input, sin re-crear el datetimepicker
    useEffect(() => {
        if (!fechaRef.current || typeof $ === "undefined") return;
        const $input = $(fechaRef.current);
        if (form.Fecha) $input.val(form.Fecha);
    }, [form.Fecha]);

    const set = (k, v) =>
        setForm((f) => ({
            ...f,
            [k]: v
        }));

    const validate = () => {
        const e = {};

        if (!String(form.Placa).trim()) e.Placa = "Requerido";
        if (!String(form.Device).trim()) e.Device = "Requerido";
        if (!String(form.Evento).trim()) e.Evento = "Requerido";

        if (!form.Fecha) {
            e.Fecha = "Requerido";
        } else if (
            !/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(String(form.Fecha))
        ) {
            e.Fecha = "Fecha/hora inválida";
        }

        const lat = numberOr(form.Latitud, null),
            lon = numberOr(form.Longitud, null);
        if (lat == null || lat < -90 || lat > 90)
            e.Latitud = "Latitud inválida";
        if (lon == null || lon < -180 || lon > 180)
            e.Longitud = "Longitud inválida";


        return e;
    };

    const handleSubmit = (ev) => {
        ev.preventDefault();
        const e = validate();
        setErrors(e);
        if (Object.keys(e).length) return;

        const payload = {
            IDPhoto: numberOr(form.IDPhoto, null),
            Placa: form.Placa.trim(),
            // Enviamos EXACTAMENTE la cadena que maneja el datetimepicker:
            // "YYYY-MM-DD HH:MM:SS" en 24h, sin cambios de zona
            Fecha: form.Fecha,
            Device: form.Device.trim(),
            Evento: form.Evento.trim(),
            Latitud: numberOr(form.Latitud, 0),
            Longitud: numberOr(form.Longitud, 0),
            Geocerca: numberOr(form.Geocerca, 0)
        };

        axios
            .post(
                `${process.env.REACT_APP_SERVER}/operaciones2/setfotostraka`,
                JSON.stringify(payload),
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem(
                            "tkn"
                        )}`
                    },
                    withCredentials: true
                }
            )
            .then((response) => {
                if (response.data?.success) {
                    Swal.fire({
                        icon: "success",
                        title: "Éxito",
                        text: "Foto Traka guardada exitosamente."
                    }).then(() => onClose?.());
                } else {
                    Swal.fire({
                        icon: "error",
                        title: "Error",
                        text:
                            response.data?.message ||
                            "Error del servidor."
                    });
                }
            })
            .catch((error) => {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: error?.message || "Error desconocido."
                });
            });
    };

    // useEffect(() => {
    //     if (geocercas.length > 0 && !form.Geocerca) {
    //         setForm((f) => ({ ...f, Geocerca: mapGeo(geocercas[0]).id }));
    //         setErrors((e) => ({ ...e, Geocerca: null }));
    //     }
    // }, [geocercas]);

    return (
        <div className="modal-fit">
            <div className="modal-scroll">
                <Form onSubmit={handleSubmit}>
                    {/* ===== Grid 12 cols (desktop) con gaps consistentes ===== */}
                    <div className="form-grid">
                        {/* Fila 1 */}
                        <div className="item span-6">
                            <FormGroup className="mb-0">
                                <Label
                                    htmlFor="IDPhoto"
                                    className="mb-1 text-uppercase small fw-semibold"
                                >
                                    IDPhoto
                                </Label>
                                <Input
                                    id="IDPhoto"
                                    type="number"
                                    bsSize="sm"
                                    value={form.IDPhoto}
                                    disabled
                                />
                            </FormGroup>
                        </div>
                        <div className="item span-6">
                            <FormGroup className="mb-0">
                                <Label
                                    htmlFor="Placa"
                                    className="mb-1 text-uppercase small fw-semibold"
                                >
                                    Placa
                                </Label>
                                <Input
                                    id="Placa"
                                    type="text"
                                    bsSize="sm"
                                    value={form.Placa}
                                    onChange={(e) =>
                                        set("Placa", e.target.value)
                                    }
                                />
                                {errors.Placa && (
                                    <small className="text-danger d-block mt-1">
                                        {errors.Placa}
                                    </small>
                                )}
                            </FormGroup>
                        </div>

                        {/* Fila 2 */}
                        <div className="item span-6">
                            <FormGroup className="mb-0">
                                <Label
                                    htmlFor="Fecha"
                                    className="mb-1 text-uppercase small fw-semibold"
                                >
                                    Fecha (24h)
                                </Label>
                                <Input
                                    id="Fecha"
                                    type="text"
                                    bsSize="sm"
                                    innerRef={fechaRef}
                                    defaultValue={form.Fecha}
                                    onChange={(e) =>
                                        set("Fecha", e.target.value)
                                    }
                                />
                                {errors.Fecha && (
                                    <small className="text-danger d-block mt-1">
                                        {errors.Fecha}
                                    </small>
                                )}
                                <small className="text-muted d-block mt-1">
                                    Formato: YYYY-MM-DD HH:MM:SS (24 horas)
                                </small>
                            </FormGroup>
                        </div>
                        <div className="item span-6">
                            <FormGroup className="mb-0">
                                <Label
                                    htmlFor="Device"
                                    className="mb-1 text-uppercase small fw-semibold"
                                >
                                    Device
                                </Label>
                                <Input
                                    id="Device"
                                    type="text"
                                    bsSize="sm"
                                    value={form.Device}
                                    onChange={(e) =>
                                        set("Device", e.target.value)
                                    }
                                />
                                {errors.Device && (
                                    <small className="text-danger d-block mt-1">
                                        {errors.Device}
                                    </small>
                                )}
                            </FormGroup>
                        </div>

                        {/* Evento */}
                        <div className="item span-12">
                            <FormGroup className="mb-0">
                                <Label
                                    htmlFor="Evento"
                                    className="mb-1 text-uppercase small fw-semibold"
                                >
                                    Evento
                                </Label>
                                <Input
                                    id="Evento"
                                    type="text"
                                    bsSize="sm"
                                    value={form.Evento}
                                    onChange={(e) =>
                                        set("Evento", e.target.value)
                                    }
                                />
                                {errors.Evento && (
                                    <small className="text-danger d-block mt-1">
                                        {errors.Evento}
                                    </small>
                                )}
                            </FormGroup>
                        </div>

                        {/* Fila 3 */}
                        <div className="item span-6">
                            <FormGroup className="mb-0">
                                <Label
                                    htmlFor="Latitud"
                                    className="mb-1 text-uppercase small fw-semibold"
                                >
                                    Latitud
                                </Label>
                                <Input
                                    id="Latitud"
                                    type="number"
                                    step="any"
                                    bsSize="sm"
                                    value={form.Latitud}
                                    onChange={(e) =>
                                        set("Latitud", e.target.value)
                                    }
                                />
                                {errors.Latitud && (
                                    <small className="text-danger d-block mt-1">
                                        {errors.Latitud}
                                    </small>
                                )}
                            </FormGroup>
                        </div>
                        <div className="item span-6">
                            <FormGroup className="mb-0">
                                <Label
                                    htmlFor="Longitud"
                                    className="mb-1 text-uppercase small fw-semibold"
                                >
                                    Longitud
                                </Label>
                                <Input
                                    id="Longitud"
                                    type="number"
                                    step="any"
                                    bsSize="sm"
                                    value={form.Longitud}
                                    onChange={(e) =>
                                        set("Longitud", e.target.value)
                                    }
                                />
                                {errors.Longitud && (
                                    <small className="text-danger d-block mt-1">
                                        {errors.Longitud}
                                    </small>
                                )}
                            </FormGroup>
                        </div>

                        {/* Geocerca */}
                        <div className="item span-6">
                            <FormGroup className="mb-0">
                                <Label className="mb-1 text-uppercase small fw-semibold">
                                    Geocerca
                                </Label>
                                <GeocercaSelect
                                    options={geoOptions}
                                    value={Number(form.Geocerca) === 0 ? "" : Number(form.Geocerca)}
                                    onChange={(id) => set("Geocerca", id)}
                                    bsSize="sm"
                                />
                                {errors.Geocerca && (
                                    <small className="text-danger d-block mt-1">
                                        {errors.Geocerca}
                                    </small>
                                )}
                            </FormGroup>
                        </div>
                        <div className="item span-6" aria-hidden="true"></div>
                    </div>

                    {/* Botones */}
                    <div className="buttons mt-3">
                        <Button
                            type="button"
                            color="secondary"
                            outline
                            onClick={onClose}
                            className="btn-action"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            color="primary"
                            className="btn-action"
                        >
                            Guardar
                        </Button>
                    </div>
                </Form>
            </div>

            {/* ==== Estilos ==== */}
            <style jsx>{`
                .modal-fit {
                    inline-size: 100%;
                    max-inline-size: min(100vw - 24px, 920px);
                    margin-inline: auto;
                    box-sizing: border-box;
                    overflow-x: hidden;
                }
                .modal-scroll {
                    max-block-size: calc(100dvh - 140px);
                    overflow: auto;
                    padding: 16px 20px;
                    box-sizing: border-box;
                    scrollbar-gutter: stable both-edges;
                }

                .form-grid {
                    --col-gap: 50px;
                    --row-gap: 22px;
                    display: grid;
                    grid-template-columns: 1fr;
                    column-gap: var(--col-gap);
                    row-gap: var(--row-gap);
                }
                .item {
                    min-width: 0;
                }
                .span-6 {
                    grid-column: 1 / -1;
                }
                .span-12 {
                    grid-column: 1 / -1;
                }

                @media (min-width: 992px) {
                    .form-grid {
                        grid-template-columns: repeat(
                            12,
                            minmax(0, 1fr)
                        );
                    }
                    .span-6 {
                        grid-column: span 6;
                    }
                    .span-12 {
                        grid-column: span 12;
                    }
                }

                :global(.modal-fit .form-control),
                :global(.modal-fit .form-select),
                :global(.modal-fit textarea.form-control) {
                    width: 100% !important;
                    max-width: 100% !important;
                    box-sizing: border-box !important;
                }

                .geo-wrap {
                    inline-size: 100%;
                }
                .geo-input {
                    inline-size: 100%;
                }
                .geo-menu {
                    left: 0;
                    right: 0;
                }

                .buttons {
                    display: flex;
                    gap: 12px;
                    justify-content: flex-end;
                    width: 100%;
                }
                .buttons :global(.btn-action) {
                    min-width: 120px;
                    height: 40px;
                    box-sizing: border-box;
                }
                @media (max-width: 576px) {
                    .buttons {
                        flex-direction: column;
                        align-items: stretch;
                    }
                    .buttons :global(.btn-action) {
                        width: 100%;
                        min-width: 0;
                    }
                }
            `}</style>
        </div>
    );
}
